import React, { FunctionComponent, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
// import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useMutation } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next'
// import useMediaQuery from '@mui/material/useMediaQuery';

// import { check_subdomain } from '../../lib/server_helper';
import { fetch_one } from "../../../../lib/v31lib";
import { loginAtom } from '../../../../lib/auth';
import { currentPracticeAtom } from '../../../../lib/practice';

import Add from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import EditIcon from '@mui/icons-material/Edit';
import MailIcon from '@mui/icons-material/Mail';

import {
  Avatar,
  Box,
  Button as Btn,
  Chip,
  DialogActions,
  Grid,
  Paper,
  Tooltip,
} from '@mui/material';

import {
  Editor,
  IconButton,
  PremiumOverlayIcon,
  TagChip,
  Typography
} from '../../../../components/v2/styled';
import { Base } from '../../../../components/v2/dialogs/Base';

import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';
import HelpersEhealth from '../../../../actions/helpers/ehealth';
import HelpersPractice from '../../../../actions/helpers/practice';
import HelpersZorgmail from '../../../../actions/helpers/zorgmail';

import { User } from '../../../../models/User';
import { CoachTagLinker } from '../../../../components/v2/dialogs/CoachtagLinker';

// Per-icon-link render config: the gate (runtime availability for the viewed
// client — client type, ehealth/zorgmail role) and the element to render.
// Kept here (UI concern) rather than in the helper, which only owns data/logic
// + module-gating — same split as GotoLinks' LINK_RENDER and Actions'
// ACTION_RENDER. Module on/off is already resolved into `item.state` by the
// helper (disabled links never reach `render`).
//
// Note on the FM link: the original widget rendered it as an <Avatar> with a
// remote vinkje image rather than an MUI icon. We keep that exact element here.
type IconLinkCtx = {
  object: User;
  login: any;
  currentPractice: any;
  navigate: (path: string, opts?: any) => void;
  setPremiumEhealthOpen: (b: boolean) => void;
  setPremiumZorgmailOpen: (b: boolean) => void;
  setPremiumNutriMailOpen: (b: boolean) => void;
  setEhealthInitOpen: (b: boolean) => void;
  setZorgmailInitOpen: (b: boolean) => void;
  is_nutri_mail: boolean;
  is_ehealth: boolean;
  is_zorgmail: boolean;
  has_ehealth: boolean;
  has_zorgmail: boolean;
  t: any;
};

type IconLinkRender = {
  gate: (ctx: IconLinkCtx) => boolean;
  render: (ctx: IconLinkCtx) => React.ReactNode;
};

const ICON_LINK_RENDER: Record<string, IconLinkRender> = {
  fm: {
    gate: (ctx) => ctx.object?.type === "User::User"
      && Object.keys(ctx.object.full_fm_links || {}).indexOf(ctx.currentPractice?.id?.toString() || '') > -1,
    render: (ctx) => <Tooltip title={ctx.t("coach.general.actions.open_in_fm", "Openen in FileMaker")}>
      <IconButton onClick={(event) => {
        event.preventDefault();
        window.open((ctx.object.full_fm_links || {})[(ctx.currentPractice?.id?.toString() || '')], "_BLANK")
      }}>
        <Avatar src="https://cdn.kangacoders.com/direct/nutrilink_eu/images/vinkje_blauw_groen.png" />
      </IconButton>
    </Tooltip>,
  },

  logs: {
    gate: (ctx) => ctx.object?.type === "User::User",
    render: (ctx) => <Tooltip title={ctx.t("coach.general.actions.open_logs", "Open de logs van deze client")}>
      <IconButton onClick={(event) => {
        event.preventDefault();
        ctx.navigate(`/logs`, {state: {user_id: ctx.object.id, first_name: ctx.object.first_name, last_name: ctx.object.last_name}});
      }}>
        <AssignmentIcon />
      </IconButton>
    </Tooltip>,
  },

  nutri_mail: {
    gate: () => true,
    render: (ctx) => <Tooltip title={ctx.t("coach.general.actions.open_mails", "Open de NutriMails met deze client")}>
      <IconButton onClick={(event) => {
        event.preventDefault();
        if (ctx.is_nutri_mail) {
          ctx.navigate(`/inbox`, {state: {user_id: {id: ctx.object.id, name: `${ctx.object.first_name} ${ctx.object.last_name}`}}});
        } else {
          ctx.setPremiumNutriMailOpen(true);
        }
      }}>
        <PremiumOverlayIcon isPremium={!!ctx.is_nutri_mail} Icon={MailIcon} />
      </IconButton>
    </Tooltip>,
  },

  ehealth: {
    gate: (ctx) => HelpersEhealth.can_check_ehealth(ctx.login),
    render: (ctx) => <Tooltip title={ctx.t("coach.general.actions.open_ehealth_mails", "Open de EhealthMails met deze client")}>
      <IconButton onClick={(event) => {
        event.preventDefault();
        if (ctx.is_ehealth) {
          if (ctx.has_ehealth) {
            ctx.navigate(`/ehealth_inbox`, {state: {user_id: {id: ctx.object.id, name: `${ctx.object.first_name} ${ctx.object.last_name}`}, linked_client: {value: ctx.object.id, label: `${ctx.object.first_name} ${ctx.object.last_name}`, rrn: ctx.object.national_number}}});
          } else {
            ctx.setEhealthInitOpen(true);
          }
        } else {
          ctx.setPremiumEhealthOpen(true);
        }
      }}>
        <PremiumOverlayIcon isPremium={!!ctx.is_ehealth} Icon={ContactMailIcon} />
      </IconButton>
    </Tooltip>,
  },

  zorgmail: {
    gate: (ctx) => HelpersZorgmail.can_check_zorgmail(ctx.login),
    render: (ctx) => <Tooltip title={ctx.t("coach.general.actions.open_zorgmails", "Open de ZorgMails met deze client")}>
      <IconButton onClick={(event) => {
        event.preventDefault();
        if (ctx.is_zorgmail) {
          if (ctx.has_zorgmail) {
            ctx.navigate(`/zorgmail_inbox`, {state: {user_id: {id: ctx.object.id, name: `${ctx.object.first_name} ${ctx.object.last_name}`}, linked_client: {value: ctx.object.id, label: `${ctx.object.first_name} ${ctx.object.last_name}`, rrn: ctx.object.national_number}}});
          } else {
            ctx.setZorgmailInitOpen(true);
          }
        } else {
          ctx.setPremiumZorgmailOpen(true);
        }
      }}>
        <PremiumOverlayIcon isPremium={!!ctx.is_zorgmail} Icon={ContactMailIcon} />
      </IconButton>
    </Tooltip>,
  },

  eid: {
    gate: (ctx) => ctx.object?.type === "User::User",
    render: (ctx) => <Tooltip title={ctx.t("general.eid.read_card_action")}>
      <IconButton onClick={(event) => {
        event.preventDefault();
        try {
          sessionStorage.setItem('eid_target_user', String(ctx.object.id));
        } catch (_e) {}
        ctx.navigate('/eid/landing');
      }}>
        <CreditCardIcon />
      </IconButton>
    </Tooltip>,
  },
};

// const { DateTime } = require("luxon");

type Props = {
  object: User;
  setPremiumEhealthOpen: (b:boolean) => void;
  setPremiumZorgmailOpen: (b:boolean) => void;
  setPremiumNutriMailOpen: (b:boolean) => void;
  setEhealthInitOpen: (b:boolean) => void;
  setZorgmailInitOpen: (b:boolean) => void;
  objectRefetch: () => void;
}

export const CoachUserWidgetsPictureAndTags: FunctionComponent<Props> = ({
  object, objectRefetch,
  setPremiumEhealthOpen, setPremiumZorgmailOpen, setPremiumNutriMailOpen,
  setEhealthInitOpen, setZorgmailInitOpen
}) => {
  const [login] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const theme = useTheme();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);
  // const params = useParams();
  // const queryClient = useQueryClient();
  // const { state } = useLocation();
  // const [searchParams, setSearchParams] = useSearchParams();
  
  const [tagLinkerOpen, setTagLinkerOpen] = useState<boolean>(false);
  const [reasonEditOpen, setReasonEditOpen] = useState<boolean>(false);
  const [reasonDraft, setReasonDraft] = useState<string>('');

  const intakeProfilePracticeId = object.intake_profile_practice?.id;

  const mutationSaveReason = useMutation({
    mutationFn: (pars: {
      formData: any;
    }) => {
      return fetch_one(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/save`,
        pars.formData,
        login
      );
    },
    onSuccess: (data, variables, context) => {
      objectRefetch();
    },
  });


  let is_nutri_mail = !!HelpersPractice.practice_modules_v3({practice: currentPractice, check: 'nutri_mail'});
  let is_ehealth = !!HelpersPractice.practice_modules_v3({practice: currentPractice, check: 'ehealth'});
  let is_zorgmail = !!HelpersPractice.practice_modules_v3({practice: currentPractice, check: 'zorg_mail'});

  let has_ehealth = !!HelpersEhealth.ehealth_initialised(login);
  let has_zorgmail = !!HelpersZorgmail.zorgmail_initialised(login);

  // picture_and_tags settings are a coach preference, stored alongside the
  // widget layout on login.settings.userdetails_widgets.settings. The value
  // is composite: { sections: Record<string,boolean>, sections_order: string[], icon_links: Record<string,boolean> }.
  // `picture_and_tags_order` (optional) overrides the icon-link order.
  const widgetSettings = login?.settings?.userdetail_widgets?.settings;
  const patValue = widgetSettings?.[HelpersClientWidgets.pictureAndTagsValueKey] || {};
  const sections = patValue?.sections || {};
  const sectionsOrder = patValue?.sections_order;
  const iconLinks = patValue?.icon_links || {};
  const iconLinksOrder = widgetSettings?.[HelpersClientWidgets.pictureAndTagsOrderKey];

  // Section visibility (default visible unless explicitly hidden).
  const orderedSections = HelpersClientWidgets.getOrderedPictureAndTagsSections(sectionsOrder, sections)

  // Icon-link items in the coach's saved order, state-tagged (active/inactive/
  // disabled). We render only 'active' links that also pass their runtime gate.
  const orderedIconLinks = HelpersClientWidgets.getOrderedPictureAndTagsIconLinks(iconLinksOrder, iconLinks, currentPractice);

  const iconLinkCtx: IconLinkCtx = {
    object, login, currentPractice, navigate,
    setPremiumEhealthOpen, setPremiumZorgmailOpen, setPremiumNutriMailOpen,
    setEhealthInitOpen, setZorgmailInitOpen,
    is_nutri_mail, is_ehealth, is_zorgmail, has_ehealth, has_zorgmail, t,
  };

  const sectionNodes: Record<string, React.ReactNode> = {
    picture: <Avatar sx={{
      height: 120,
      width: 120,
      marginLeft: 'auto',
      marginRight: 'auto',
      display: 'block'
    }} src={object.get_medium}></Avatar>,
    name: <Typography variant="h5" sx={{marginTop: 1}}>{object.first_name} {object.last_name}</Typography>,
    icon_links: <Box>
      {orderedIconLinks.map((item) => {
        if (item.state !== 'active') return null; // module off or coach hid
        const cfg = ICON_LINK_RENDER[item.key];
        if (!cfg || !cfg.gate(iconLinkCtx)) return null;
        return <React.Fragment key={item.key}>{cfg.render(iconLinkCtx)}</React.Fragment>;
      })}
    </Box>,
    tags: <Box>
      {(object.user_tags || []).map(tg => <TagChip tag={tg} />)}
      <Chip sx={{backgroundColor: theme.palette.primary.main, "& > span": {paddingLeft: 0}}} onClick={() => {
        setTagLinkerOpen(true);
      }} avatar={<Add sx={{color: 'white !important'}} />} />
    </Box>,
    consult_reason: <>
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, marginTop: 1}}>
        <Typography variant="subtitle1" sx={{fontWeight: 'bold'}}>{t("coach.user_detail.consult_reason")}</Typography>
        {!!intakeProfilePracticeId && <Tooltip title={t("coach.general.actions.edit", "Bewerken")}>
          <IconButton sx={{padding: 0.5}} onClick={() => {
            setReasonDraft(object.intake_profile_practice?.consult_reason || '');
            setReasonEditOpen(true);
          }}>
            <EditIcon sx={{fontSize: '1rem'}} />
          </IconButton>
        </Tooltip>}
      </Box>
      {!!object.intake_profile_practice?.consult_reason
        ? <Typography variant="body1" sx={{fontStyle: 'italic'}} html>{object.intake_profile_practice?.consult_reason}</Typography>
        : <Typography variant="body1" sx={{fontStyle: 'italic', color: 'text.secondary'}}>{t("coach.user_detail.consult_reason_empty", "Nog niet ingevuld")}</Typography>}
    </>,
  };









  return <Paper sx={{padding: 2, marginTop: 1}}>
    <Grid container spacing={1}>
      <Grid item xs={12} sx={{textAlign: 'center'}}>
        {orderedSections
          .filter((item) => item.state === 'shown')
          .map((item) => <React.Fragment key={item.key}>{sectionNodes[item.key]}</React.Fragment>)}
      </Grid>

    </Grid>

    {!!object?.id && !!(login?.practice_id || currentPractice?.id) && <CoachTagLinker
      open={!!tagLinkerOpen}
      setOpen={(b) => {
        objectRefetch();
        setTagLinkerOpen(b);
      }}
      userId={object?.id}
      // @ts-ignore
      practiceId={(login?.practice_id || currentPractice?.id)}
      onClose={() => {
        objectRefetch();
      }}
    />}

    <Base
      name="consultReasonEdit"
      hidden={!reasonEditOpen}
      maxWidth="xl"
      content={<Grid container spacing={1}>
        <Grid item xs={12}>
          <Editor
            simple
            label={t("coach.models.intake_profiles.consult_reason", "Reden van consultatie")}
            value={reasonDraft}
            onChange={setReasonDraft}
          />
        </Grid>
      </Grid>}
      actions={<DialogActions sx={{borderTop: '1px solid transparent', borderColor: theme.palette.primary.main}}>
        <Btn autoFocus onClick={() => {setReasonEditOpen(false);}}>{t("coach.general.actions.cancel")}</Btn>
        <Btn
          disabled={mutationSaveReason.isPending}
          onClick={() => {
            mutationSaveReason.mutate({
              formData: {
                object: 'intake_profile',
                id: intakeProfilePracticeId,
                ob: {
                  id: intakeProfilePracticeId,
                  consult_reason: reasonDraft
                },
                fields: ['id']
              }
            });
            setReasonEditOpen(false);
          }}>{t("coach.general.actions.save")}</Btn>
      </DialogActions>}
      open={reasonEditOpen}
      setOpen={setReasonEditOpen}
    />
  </Paper>;
}
